/*
 * Product image viewer - Mosaic.
 *
 * Two parts, in order:
 *
 *   1. GalleryMedia - the single source of truth for "which media belong to
 *      the variant the shopper has selected". Rubik Variant Images owns that
 *      decision; it publishes it by hiding the tiles it does not want. This
 *      part reads that decision back out of the gallery, keeps it alive across
 *      the slider tearing its own markup down, and republishes it as an event.
 *
 *   2. The viewer - a fixed overlay holding one image per screen in a vertical
 *      scroll-snap column, built from whatever GalleryMedia currently reports.
 *      Its look is unchanged from the Baseline treatment it was ported from:
 *      full-width image, progress marks down the left edge, close button top
 *      right, scheme background, scroll lock on <html> while open.
 *
 * Why the interaction layer was rewritten rather than patched:
 *
 *   The old viewer expressed zoom as "drop the fit-to-width sizing so the
 *   image renders at natural size", which meant putting scroll-snap-align:none
 *   on the slide the shopper was looking at. Inside a scroll-snap-type:y
 *   mandatory scroller, removing the current slide's snap alignment makes the
 *   browser immediately re-snap to the next slide that still has one. Zoom and
 *   "next image" were therefore the same code path: tapping the image to zoom
 *   navigated instead, on both mobile and desktop, and there was no way to fix
 *   one without the other. Zoom is now a transform on the image, which the
 *   scroller cannot react to, so the two are finally independent.
 *
 *   Gestures are resolved in one place, from pointer events, with an explicit
 *   mode per gesture and a strict priority: two pointers is always a pinch, a
 *   single pointer while zoomed is always a pan, and only at default zoom does
 *   a deliberate drag become navigation. A tap is never navigation.
 */
(function () {
  'use strict';

  var HTML_OPEN = 'bzoom-open';

  var GALLERY = '.product-gallery';
  var TILE = '.product-gallery-item';
  var SOURCE = 'product-image-zoom[data-image]';

  var MIN_SCALE = 1;
  var MAX_SCALE = 4;
  var CLICK_SCALE = 2.5;

  var TAP_SLOP = 10; // px of travel still counted as a tap, not a drag
  var INTENT_SLOP = 8; // px of travel before a drag is given a direction
  var SWIPE_DISTANCE = 50; // px of horizontal travel that commits to prev/next
  var FLICK_VELOCITY = 0.4; // px/ms of vertical travel that commits to prev/next

  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  /* ==================================================================
   * 1. GalleryMedia
   * ================================================================== */

  function gallery() {
    return document.querySelector(GALLERY);
  }

  /* Rubik hides a tile three ways at once, and the theme's own slider rewrites
     data-index underneath it, so all three are treated as the same signal. */
  function isTileHidden(tile) {
    if (tile.classList.contains('hidden')) return true;
    if (tile.style.display === 'none') return true;
    if (tile.getAttribute('data-index') === '-1') return true;
    return false;
  }

  function tiles() {
    var root = gallery();
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll(TILE));
  }

  function activeTiles() {
    return tiles().filter(function (tile) {
      return !isTileHidden(tile);
    });
  }

  function sourceOf(tile) {
    var node = tile.querySelector(SOURCE);
    return node ? node.getAttribute('data-image') : null;
  }

  /* Video and model tiles have no zoom source; the viewer has never shown
     them, so they are excluded here rather than filtered out later. */
  function activeImageTiles() {
    return activeTiles().filter(function (tile) {
      return !!sourceOf(tile);
    });
  }

  function activeSources() {
    return activeImageTiles().map(sourceOf);
  }

  function sliderEnabled() {
    var slider = document.querySelector(GALLERY + ' css-slider');
    return !!(slider && slider.sliderEnabled);
  }

  var lastHidden = null; // media ids Rubik last hid, or null if it never has
  var lastSources = null;

  function readHidden() {
    var found = tiles();
    if (!found.length) return null;
    var hidden = [];
    found.forEach(function (tile) {
      if (isTileHidden(tile) && tile.dataset.mediaId) hidden.push(tile.dataset.mediaId);
    });
    return hidden;
  }

  /* The slider restores a snapshot of its own markup whenever it crosses the
     767px breakpoint (destroySlider assigns back over innerHTML), which throws
     away whatever Rubik had hidden since that snapshot was taken. Reapplying
     the last known decision is not a second opinion about which media belong
     to the variant - it is the same decision, put back.

     Every write here is conditional. An unconditional one would come straight
     back through the observer that called this and drive an endless resync. */
  function applyHidden() {
    if (!lastHidden) return;
    tiles().forEach(function (tile) {
      var hide = lastHidden.indexOf(tile.dataset.mediaId) !== -1;
      if (hide) {
        if (tile.style.display !== 'none') tile.style.display = 'none';
        if (tile.getAttribute('data-index') !== '-1') tile.setAttribute('data-index', '-1');
        tile.classList.add('hidden');
      } else {
        if (tile.style.display === 'none') tile.style.display = '';
        tile.classList.remove('hidden');
      }
    });
  }

  /* While the slider runs it renumbers data-index itself, over its own filtered
     item list, and the thumbnail rail follows. Above 767px Mosaic keeps the
     slider switched off, so nothing renumbers and the rail's indices point at
     the wrong tiles as soon as any media is hidden. Fill that gap only. */
  function reindex() {
    if (sliderEnabled()) return;
    activeTiles().forEach(function (tile, i) {
      if (tile.getAttribute('data-index') !== String(i)) {
        tile.setAttribute('data-index', String(i));
      }
    });
  }

  /* Mosaic only switches the product gallery's slider on below 767px, through
     the slider component's watchCSS option. Above that the component never
     mounts, so it never publishes its slide list on `items` - and Rubik's
     adapter for this theme family reads exactly that property to decide what to
     hide. Its `if (!sliderItems) return` is the whole reason variant filtering
     reaches the mobile gallery and silently misses the desktop one.

     Two things are missing, and both belong to the slider rather than to the
     viewer, so they are applied to the element itself instead of by editing the
     theme's component: that keeps every carousel on the rest of the store out
     of the blast radius, and a theme update cannot quietly undo them.

       - publish the slide list whenever the slider is not mounted, so the
         filtering has something to enumerate;
       - make resetSlider and changeSlide no-ops while it is not mounted, since
         both reach for a viewport, holder and container that exist only after
         initSlider has run. Rubik calls both immediately after handing over its
         filtered list, and on desktop they would otherwise throw. */
  function primeSlider() {
    var slider = document.querySelector(GALLERY + ' css-slider');
    if (!slider) return;

    if (!slider._galleryGuarded && typeof slider.resetSlider === 'function') {
      slider._galleryGuarded = true;
      ['resetSlider', 'changeSlide'].forEach(function (name) {
        var original = slider[name];
        if (typeof original !== 'function') return;
        slider[name] = function () {
          if (!this.element || !this.element.isConnected) return;
          return original.apply(this, arguments);
        };
      });
    }

    /* destroySlider assigns back over innerHTML, so a list captured before the
       breakpoint was crossed points at detached nodes. Treat that as absent. */
    var stale = !slider.items || !slider.items.length || !slider.items[0].isConnected;
    if (!slider.sliderEnabled && stale) {
      slider.items = slider.querySelectorAll(TILE);
      slider.length = slider.items.length;
    }
  }

  function sameList(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  function publish() {
    var sources = activeSources();
    if (sameList(sources, lastSources)) return;
    lastSources = sources;
    document.dispatchEvent(new CustomEvent('gallery:media-change', { detail: { sources: sources } }));
  }

  var syncQueued = false;
  var rebuilt = false;

  function sync() {
    var wasRebuild = rebuilt;
    rebuilt = false;
    var hidden = readHidden();
    /* The gallery is the authority on what is filtered, with one exception: a
       rebuild that dropped a filter which was in force. Only then is the last
       known decision written back, and only ever back onto an unfiltered
       gallery, so this can never overrule a fresh decision from Rubik. */
    if (wasRebuild && lastHidden && lastHidden.length && hidden && !hidden.length) {
      applyHidden();
    } else if (hidden) {
      lastHidden = hidden;
    }
    primeSlider();
    reindex();
    markTiles();
    publish();
  }

  function queueSync(fromRebuild) {
    if (fromRebuild) rebuilt = true;
    if (syncQueued) return;
    syncQueued = true;
    /* A variant can be changed in a background tab, where requestAnimationFrame
       never fires, so the frame is the fast path and the timer is the floor. */
    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      syncQueued = false;
      sync();
    };
    if (window.requestAnimationFrame) requestAnimationFrame(run);
    setTimeout(run, 120);
  }

  function touchesTiles(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType !== 1) continue;
      if (node.matches && node.matches(TILE)) return true;
      if (node.querySelector && node.querySelector(TILE)) return true;
    }
    return false;
  }

  function watchGallery() {
    var root = gallery();
    if (!root || !('MutationObserver' in window)) return;

    new MutationObserver(function (records) {
      var structural = false;
      for (var i = 0; i < records.length; i++) {
        if (records[i].type !== 'childList') continue;
        /* Lazy images swap nodes in below the tiles all the time; only the
           tiles being replaced means the slider rebuilt the gallery. */
        if (touchesTiles(records[i].addedNodes) || touchesTiles(records[i].removedNodes)) {
          structural = true;
          break;
        }
      }
      queueSync(structural);
    }).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-index']
    });
  }

  /* ==================================================================
   * 2. The viewer
   * ================================================================== */

  var overlay = null;
  var scroller = null;
  var marks = null;
  var slides = [];
  var sources = [];
  var lastFocus = null;
  var index = 0;
  var stale = false;

  function destroy() {
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    overlay = scroller = marks = null;
    slides = [];
  }

  /* Every listener below hangs off nodes created here, so the teardown above
     takes them with it. Opening and closing the viewer does not rebuild
     anything, and a rebuild cannot leave a second copy behind. */
  function build(urls) {
    destroy();
    sources = urls.slice();

    overlay = document.createElement('div');
    overlay.className = 'bzoom';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.tabIndex = -1;
    overlay.hidden = true;

    scroller = document.createElement('div');
    scroller.className = 'bzoom__scroller';

    slides = [];
    urls.forEach(function (url, i) {
      var slide = document.createElement('div');
      slide.className = 'bzoom__slide';

      var img = document.createElement('img');
      img.className = 'bzoom__img';
      img.src = url;
      img.alt = '';
      img.decoding = 'async';
      img.draggable = false;
      if (i > 1) img.loading = 'lazy';

      slide.appendChild(img);
      scroller.appendChild(slide);
      slide._zoom = { scale: 1, x: 0, y: 0 };
      slides.push(slide);
    });

    marks = document.createElement('div');
    marks.className = 'bzoom__marks';
    marks.setAttribute('aria-hidden', 'true');
    urls.forEach(function () {
      var mark = document.createElement('span');
      mark.className = 'bzoom__mark';
      marks.appendChild(mark);
    });

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'bzoom__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML =
      '<svg viewBox="0 0 21 21" width="21" height="21" fill="none" focusable="false" aria-hidden="true">' +
      '<path d="M3.5 3.5 17.5 17.5M17.5 3.5 3.5 17.5" stroke="currentColor" stroke-width="1"/></svg>';
    close.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      hide();
    });

    overlay.appendChild(scroller);
    overlay.appendChild(marks);
    overlay.appendChild(close);
    document.body.appendChild(overlay);

    scroller.addEventListener('scroll', onScroll, { passive: true });
    bindGestures();
  }

  /* One slide fills the scroller, so the visible index is the scroll offset
     over the slide height. Baseline brightened the matching mark the same way,
     off an intersection observer. */
  function currentIndex() {
    if (!scroller || !scroller.clientHeight) return 0;
    return clamp(Math.round(scroller.scrollTop / scroller.clientHeight), 0, slides.length - 1);
  }

  function syncMarks(i) {
    if (!marks) return;
    for (var m = 0; m < marks.children.length; m++) {
      marks.children[m].classList.toggle('is-current', m === i);
    }
  }

  /* A smooth scroll fires this all the way to its destination. Letting those
     intermediate positions write back to `index` would make the next goTo -
     a second arrow press, a second swipe - start from wherever the animation
     had got to rather than from where it is heading. */
  var arriveBy = 0;

  function onScroll() {
    if (Date.now() < arriveBy) {
      syncMarks(index);
      return;
    }
    var next = currentIndex();
    if (next === index) return;
    index = next;
    resetZoom(); // a new image always starts at its default scale
    syncMarks(index);
  }

  function goTo(i, smooth) {
    if (!slides.length) return;
    index = clamp(i, 0, slides.length - 1);
    resetZoom();
    arriveBy = Date.now() + (smooth === false ? 0 : 600);
    scroller.scrollTo({ top: slides[index].offsetTop, behavior: smooth === false ? 'auto' : 'smooth' });
    syncMarks(index);
  }

  /* ---- zoom state -------------------------------------------------- */

  function zoomOf(slide) {
    return slide._zoom || (slide._zoom = { scale: 1, x: 0, y: 0 });
  }

  function bounds(slide, scale) {
    var img = slide.querySelector('.bzoom__img');
    if (!img) return { x: 0, y: 0 };
    return {
      x: Math.max(0, (img.offsetWidth * scale - slide.clientWidth) / 2),
      y: Math.max(0, (img.offsetHeight * scale - slide.clientHeight) / 2)
    };
  }

  function paint(slide, animate) {
    var img = slide.querySelector('.bzoom__img');
    if (!img) return;
    var z = zoomOf(slide);
    var limit = bounds(slide, z.scale);
    z.x = clamp(z.x, -limit.x, limit.x);
    z.y = clamp(z.y, -limit.y, limit.y);
    img.style.transition = animate ? 'transform 220ms cubic-bezier(.2,.7,.3,1)' : 'none';
    img.style.transform = 'translate(' + z.x + 'px,' + z.y + 'px) scale(' + z.scale + ')';
    slide.classList.toggle('is-zoomed', z.scale > 1.001);
    syncZoomedClass();
  }

  function syncZoomedClass() {
    if (!overlay) return;
    var any = slides.some(function (slide) {
      return zoomOf(slide).scale > 1.001;
    });
    overlay.classList.toggle('bzoom--zoomed', any);
  }

  function resetZoom() {
    slides.forEach(function (slide) {
      var z = zoomOf(slide);
      if (z.scale === 1 && z.x === 0 && z.y === 0) {
        slide.classList.remove('is-zoomed');
        return;
      }
      z.scale = 1;
      z.x = 0;
      z.y = 0;
      paint(slide, true);
    });
    if (overlay) overlay.classList.remove('bzoom--zoomed');
  }

  /* Zoom about a point so the pixel under the fingers (or the cursor) stays
     under them, which is what makes the gesture feel attached to the image. */
  function zoomAbout(slide, nextScale, px, py, animate) {
    var z = zoomOf(slide);
    var rect = slide.getBoundingClientRect();
    var qx = px - (rect.left + rect.width / 2);
    var qy = py - (rect.top + rect.height / 2);
    var ratio = nextScale / z.scale;
    z.x = qx - (qx - z.x) * ratio;
    z.y = qy - (qy - z.y) * ratio;
    z.scale = nextScale;
    paint(slide, animate);
  }

  /* ---- gestures ---------------------------------------------------- */

  var pointers = new Map();
  var gesture = null;

  function slideFromEvent(event) {
    var node = event.target;
    return node && node.closest ? node.closest('.bzoom__slide') : null;
  }

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function midpoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  function twoPointers() {
    var out = [];
    pointers.forEach(function (p) {
      out.push(p);
    });
    return out;
  }

  function beginPinch(slide) {
    var pair = twoPointers();
    if (pair.length < 2) return;
    var z = zoomOf(slide);
    gesture = {
      mode: 'pinch',
      slide: slide,
      startDistance: Math.max(1, distance(pair[0], pair[1])),
      startScale: z.scale,
      startX: z.x,
      startY: z.y,
      centre: midpoint(pair[0], pair[1])
    };
    setDragging(true);
  }

  function setDragging(on) {
    if (overlay) overlay.classList.toggle('bzoom--dragging', !!on);
  }

  function onPointerDown(event) {
    var slide = slideFromEvent(event);
    if (!slide) return;

    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, id: event.pointerId });
    try {
      event.target.setPointerCapture(event.pointerId);
    } catch (e) {}

    if (pointers.size === 2) {
      beginPinch(slide);
      return;
    }
    if (pointers.size > 2) return;

    var z = zoomOf(slide);
    gesture = {
      mode: z.scale > 1.001 ? 'pan' : 'undecided',
      slide: slide,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      startTx: z.x,
      startTy: z.y,
      startScroll: scroller.scrollTop,
      startIndex: currentIndex(),
      startTime: event.timeStamp || Date.now(),
      onImage: !!(event.target && event.target.classList && event.target.classList.contains('bzoom__img')),
      travel: 0
    };
    if (gesture.mode === 'pan') setDragging(true);
  }

  function onPointerMove(event) {
    var tracked = pointers.get(event.pointerId);
    if (!tracked) return;
    tracked.x = event.clientX;
    tracked.y = event.clientY;
    if (!gesture) return;

    /* Priority 1: two active pointers is a zoom, never anything else. */
    if (gesture.mode === 'pinch') {
      var pair = twoPointers();
      if (pair.length < 2) return;
      var scale = clamp(
        (gesture.startScale * distance(pair[0], pair[1])) / gesture.startDistance,
        MIN_SCALE * 0.6,
        MAX_SCALE
      );
      var z = zoomOf(gesture.slide);
      z.scale = gesture.startScale;
      z.x = gesture.startX;
      z.y = gesture.startY;
      var centre = midpoint(pair[0], pair[1]);
      zoomAbout(gesture.slide, scale, centre.x, centre.y, false);
      var z2 = zoomOf(gesture.slide);
      z2.x += centre.x - gesture.centre.x;
      z2.y += centre.y - gesture.centre.y;
      paint(gesture.slide, false);
      event.preventDefault();
      return;
    }

    if (event.pointerId !== gesture.pointerId) return;

    var dx = event.clientX - gesture.startX;
    var dy = event.clientY - gesture.startY;
    gesture.travel = Math.max(gesture.travel, Math.abs(dx) + Math.abs(dy));

    /* Priority 2: while the image is enlarged, one finger pans it. */
    if (gesture.mode === 'pan') {
      var zp = zoomOf(gesture.slide);
      zp.x = gesture.startTx + dx;
      zp.y = gesture.startTy + dy;
      paint(gesture.slide, false);
      event.preventDefault();
      return;
    }

    /* Priority 3: only at default zoom does a deliberate drag navigate. */
    if (gesture.mode === 'undecided') {
      if (Math.abs(dx) + Math.abs(dy) < INTENT_SLOP) return;
      gesture.mode = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'swipe' : 'scroll';
      setDragging(true);
    }

    if (gesture.mode === 'swipe') {
      var img = gesture.slide.querySelector('.bzoom__img');
      if (img) {
        img.style.transition = 'none';
        img.style.transform = 'translate(' + dx * 0.3 + 'px,0) scale(1)';
      }
      event.preventDefault();
      return;
    }

    if (gesture.mode === 'scroll') {
      scroller.scrollTop = gesture.startScroll - dy;
      gesture.lastY = event.clientY;
      event.preventDefault();
    }
  }

  function settle() {
    setTimeout(function () {
      setDragging(false);
    }, 360);
  }

  function onPointerUp(event) {
    pointers.delete(event.pointerId);
    if (!gesture) return;

    if (gesture.mode === 'pinch') {
      if (pointers.size === 1) {
        /* One finger lifted mid-pinch: carry straight on as a pan rather than
           ending the gesture, so the image does not jump. */
        var remaining = twoPointers()[0];
        var z = zoomOf(gesture.slide);
        gesture = {
          mode: 'pan',
          slide: gesture.slide,
          pointerId: remaining.id,
          startX: remaining.x,
          startY: remaining.y,
          startTx: z.x,
          startTy: z.y,
          startScroll: scroller.scrollTop,
          startIndex: currentIndex(),
          startTime: event.timeStamp || Date.now(),
          travel: TAP_SLOP + 1
        };
        return;
      }
      finishZoom(gesture.slide);
      gesture = null;
      settle();
      return;
    }

    if (event.pointerId !== gesture.pointerId) return;

    var dx = event.clientX - gesture.startX;
    var dy = event.clientY - gesture.startY;
    var elapsed = Math.max(1, (event.timeStamp || Date.now()) - gesture.startTime);
    var slide = gesture.slide;
    var mode = gesture.mode;
    var onImage = gesture.onImage;
    var travel = gesture.travel;
    var startIndex = gesture.startIndex;
    gesture = null;

    if (mode === 'pan') {
      paint(slide, false);
      finishZoom(slide);
      settle();
      if (travel > TAP_SLOP) return;
      /* A tap while zoomed returns the image to its default scale rather than
         doing nothing, which is the only way back on a device with no cursor. */
      if (onImage) resetZoom();
      return;
    }

    if (mode === 'swipe') {
      paint(slide, true);
      if (Math.abs(dx) > SWIPE_DISTANCE) goTo(startIndex + (dx < 0 ? 1 : -1));
      settle();
      return;
    }

    if (mode === 'scroll') {
      var velocity = dy / elapsed;
      var step = 0;
      if (Math.abs(dy) > scroller.clientHeight * 0.2 || Math.abs(velocity) > FLICK_VELOCITY) {
        step = dy < 0 ? 1 : -1;
      }
      goTo(startIndex + step);
      settle();
      return;
    }

    /* Nothing moved: this was a tap or a click. It must never navigate. */
    settle();
    if (travel > TAP_SLOP) return;

    if (!onImage) {
      /* The padding either side of the image is the backdrop, as before. */
      hide();
      return;
    }

    /* Desktop keeps click-to-zoom, which is the behaviour this viewer has
       always advertised through its zoom-in cursor. Touch does not, because a
       tap must not stand in for pinching. */
    if (event.pointerType === 'mouse') {
      var z2 = zoomOf(slide);
      if (z2.scale > 1.001) resetZoom();
      else zoomAbout(slide, CLICK_SCALE, event.clientX, event.clientY, true);
    }
  }

  function finishZoom(slide) {
    var z = zoomOf(slide);
    if (z.scale <= 1.02) {
      z.scale = 1;
      z.x = 0;
      z.y = 0;
      paint(slide, true);
      return;
    }
    if (z.scale > MAX_SCALE) z.scale = MAX_SCALE;
    paint(slide, true);
  }

  function onPointerCancel(event) {
    pointers.delete(event.pointerId);
    if (gesture && gesture.slide) {
      paint(gesture.slide, true);
      finishZoom(gesture.slide);
    }
    gesture = null;
    settle();
  }

  function bindGestures() {
    scroller.addEventListener('pointerdown', onPointerDown);
    scroller.addEventListener('pointermove', onPointerMove, { passive: false });
    scroller.addEventListener('pointerup', onPointerUp);
    scroller.addEventListener('pointercancel', onPointerCancel);
    scroller.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  }

  /* ---- open / close ------------------------------------------------ */

  function isOpen() {
    return !!overlay && !overlay.hidden;
  }

  function show(i) {
    var urls = activeSources();
    if (!urls.length) return;
    if (!overlay || stale || !sameList(urls, sources)) {
      build(urls);
      stale = false;
    }

    lastFocus = document.activeElement;
    overlay.hidden = false;
    document.documentElement.classList.add(HTML_OPEN);

    index = clamp(i, 0, slides.length - 1);
    arriveBy = 0;

    /* Land on the image that was tapped. Done straight away, because layout is
       live the moment the overlay is unhidden, then repeated on the next frame
       and once more shortly after so scroll-snap cannot pull it back. It
       deliberately does not rely on requestAnimationFrame alone, which does not
       fire in a background tab. */
    function land() {
      if (slides[index]) scroller.scrollTop = slides[index].offsetTop;
      syncMarks(index);
    }

    land();
    if (window.requestAnimationFrame) requestAnimationFrame(land);
    setTimeout(land, 60);
    overlay.focus({ preventScroll: true });
  }

  function hide() {
    if (!isOpen()) return;
    overlay.hidden = true;
    document.documentElement.classList.remove(HTML_OPEN);
    overlay.classList.remove('bzoom--zoomed', 'bzoom--dragging');
    pointers.clear();
    gesture = null;
    arriveBy = 0;
    resetZoom();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  /* A variant change while the viewer is open swaps its contents for the media
     the shopper is now looking at, rather than leaving the previous colour's
     images sitting there behind the overlay. */
  document.addEventListener('gallery:media-change', function (event) {
    var urls = event.detail.sources;
    if (!isOpen()) {
      stale = true;
      return;
    }
    if (sameList(urls, sources)) return;
    if (!urls.length) {
      hide();
      return;
    }
    var keep = clamp(index, 0, urls.length - 1);
    build(urls);
    stale = false;
    overlay.hidden = false;
    index = keep;
    arriveBy = 0;
    scroller.scrollTop = slides[index].offsetTop;
    syncMarks(index);
    overlay.focus({ preventScroll: true });
  });

  /* ---- opening from the gallery ------------------------------------ */

  /* The gallery is a drag slider, so a click that follows a drag must not open
     the viewer. Track the pointer and only treat it as a tap when it barely
     moved, which is what Splide does for Baseline. */
  var down = null;

  /* Mosaic's gallery takes pointer capture while dragging, which retargets
     later pointer events to the slider itself, so event.target is not always
     inside the tile. Hit-test the coordinates when that happens. */
  function tileFrom(event) {
    var node = event.target;
    var tile = node && node.closest ? node.closest(GALLERY + ' ' + TILE) : null;
    if (tile) return tile;
    var under = document.elementFromPoint(event.clientX, event.clientY);
    return under && under.closest ? under.closest(GALLERY + ' ' + TILE) : null;
  }

  function tileIndex(tile) {
    var list = activeImageTiles();
    for (var i = 0; i < list.length; i++) if (list[i] === tile) return i;
    return 0;
  }

  document.addEventListener(
    'pointerdown',
    function (event) {
      var tile = tileFrom(event);
      down = tile ? { tile: tile, x: event.clientX, y: event.clientY, moved: 0 } : null;
    },
    true
  );

  document.addEventListener(
    'pointermove',
    function (event) {
      if (!down) return;
      var moved = Math.abs(event.clientX - down.x) + Math.abs(event.clientY - down.y);
      if (moved > down.moved) down.moved = moved;
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    function () {
      down = null;
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var state = down;
      down = null;
      if (!state || isOpen() || state.moved > TAP_SLOP) return;
      var tile = tileFrom(event);
      if (!tile || tile !== state.tile) return;
      if (isTileHidden(tile) || !sourceOf(tile)) return;
      /* Skip controls nested inside the tile, but not the tile itself - it
         carries role="button" so that it announces as the trigger. */
      var control = event.target.closest ? event.target.closest('a, button, [role="button"]') : null;
      if (control && control !== tile) return;
      event.preventDefault();
      event.stopPropagation();
      show(tileIndex(tile));
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (isOpen()) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        hide();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(index + 1);
        return;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(index - 1);
        return;
      }
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var tile = event.target.closest && event.target.closest(GALLERY + ' ' + TILE);
    if (!tile || !sourceOf(tile)) return;
    event.preventDefault();
    show(tileIndex(tile));
  });

  /* Mark the tiles as controls, and keep doing so if the gallery is
     re-rendered on a variant change. */
  function markTiles() {
    tiles().forEach(function (tile) {
      if (tile.getAttribute('data-bzoom') === 'ready') return;
      tile.setAttribute('data-bzoom', 'ready');
      tile.setAttribute('role', 'button');
      tile.setAttribute('tabindex', '0');
    });
  }

  function init() {
    if (!gallery()) return;
    lastHidden = readHidden();
    lastSources = activeSources();
    primeSlider();
    reindex();
    markTiles();
    watchGallery();

    /* The slider's custom element may not have upgraded yet, and it re-reads
       the breakpoint on resize; both are cheap to re-check. */
    setTimeout(primeSlider, 0);
    setTimeout(primeSlider, 400);
    window.addEventListener('load', primeSlider);
    window.addEventListener('resize', function () {
      setTimeout(primeSlider, 250);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    destroy();
    sources = [];
    lastSources = null;
    lastHidden = null;
    init();
  });
})();